import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { IconArrowsMaximize, IconArrowsMinimize } from '@tabler/icons-react';
import {
  Button,
  Drawer,
  Group,
  IconButton,
  Paper,
  Progress,
  Stack,
  Text,
  cx,
  type DrawerProps,
  type PaperProps,
} from '@wapharma/design-system';
import { useWizardCloseGuard } from '../hooks/useWizardCloseGuard';
import { StepSwitch } from './StepSwitch';
import styles from './DrawerWizardStepCard.module.css';

/** Guarded dismiss from {@link DrawerWizardLayout}; Annuler prefers this over raw onCancel. */
const DrawerWizardRequestCloseContext = createContext<(() => void) | null>(null);

export function DrawerWizardRequestCloseProvider({
  value,
  children,
}: {
  value: () => void;
  children: ReactNode;
}) {
  return (
    <DrawerWizardRequestCloseContext.Provider value={value}>
      {children}
    </DrawerWizardRequestCloseContext.Provider>
  );
}

export function useDrawerWizardRequestClose(fallback: () => void): () => void {
  return useContext(DrawerWizardRequestCloseContext) ?? fallback;
}

/** Annuler / Précédent for import drawers — Annuler uses the layout close guard. */
export function DrawerWizardImportBackButton({
  isCancel,
  onCancel,
  onBack,
  disabled = false,
}: {
  isCancel: boolean;
  onCancel: () => void;
  onBack: () => void;
  disabled?: boolean;
}) {
  const requestClose = useDrawerWizardRequestClose(onCancel);
  return (
    <Button
      type="button"
      variant="soft"
      disabled={disabled}
      onClick={isCancel ? requestClose : onBack}
    >
      {isCancel ? 'Annuler' : 'Précédent'}
    </Button>
  );
}

function shouldIgnoreWizardEnterTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return true;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  if (tag === 'TEXTAREA' || tag === 'BUTTON' || tag === 'A' || tag === 'SELECT') return true;
  if (target.closest('button, a, [role="button"], [data-wp-enter-ignore]')) return true;
  return false;
}

export type DrawerWizardPaperProps = Omit<PaperProps, 'withBorder' | 'shadow'> & {
  children?: ReactNode;
};

/** Soft-bordered drawer/wizard card shell — hairline border + quiet popover shadow. */
export function DrawerWizardPaper({
  className,
  p = 'md',
  radius = 'comfortable',
  children,
  ...rest
}: DrawerWizardPaperProps) {
  return (
    <Paper withBorder p={p} radius={radius} className={cx(styles.card, className)} {...rest}>
      {children}
    </Paper>
  );
}

export type DrawerWizardStepCardProps = {
  title: string;
  /** @deprecated Unused — step cards show title only. */
  description?: string;
  children?: ReactNode;
  className?: string;
  gap?: 'sm' | 'md' | 'lg';
  /** @deprecated Outer Paper owns padding; kept for call-site compat. */
  padding?: 'none' | 'xs' | 'sm' | 'md' | 'lg';
  /** Stretch body to fill a flex sheet (fullscreen editors). */
  fill?: boolean;
};

/**
 * Drawer wizard step content — title + fields only.
 * Stable Paper lives on {@link DrawerWizardLayout} / {@link DrawerWizardStepSwitch}.
 */
export function DrawerWizardStepCard({
  title,
  children,
  className,
  gap = 'sm',
  fill = false,
}: DrawerWizardStepCardProps) {
  return (
    <div className={cx(styles.stepCard, fill && styles.stepCardFill, className)}>
      <p className={styles.title}>{title}</p>
      {children ? (
        <Stack
          gap={gap}
          className={cx(styles.stepBody, fill && styles.stepBodyFill)}
        >
          {children}
        </Stack>
      ) : null}
    </div>
  );
}

/** Stable Paper + login-style step switch for custom Drawer bodies. */
export function DrawerWizardStepSwitch({
  stepIndex,
  children,
  className,
  paperClassName,
  /** Stretch StepSwitch inside a flex sheet (e.g. PO lines fullscreen). */
  fill = false,
  onTransitionEnd,
}: {
  stepIndex: number;
  children: ReactNode;
  className?: string;
  paperClassName?: string;
  fill?: boolean;
  onTransitionEnd?: () => void;
}) {
  return (
    <DrawerWizardPaper
      className={cx(styles.stepSwitchPaper, fill && styles.stepSwitchPaperFill, paperClassName)}
    >
      <StepSwitch
        index={stepIndex}
        mode="snapshot"
        fill={fill}
        className={className}
        onTransitionEnd={onTransitionEnd}
      >
        {children}
      </StepSwitch>
    </DrawerWizardPaper>
  );
}

export function DrawerWizardStepMeta({ children }: { children: ReactNode }) {
  return <p className={styles.stepMeta}>{children}</p>;
}

/** Segmented progress as drawer header bottom edge — label above, bar flush. */
export function DrawerWizardHeaderProgress({
  value,
  max,
  label,
  'aria-label': ariaLabel,
}: {
  value: number;
  max: number;
  label?: string;
  'aria-label'?: string;
}) {
  return (
    <div className={styles.headerProgressBlock}>
      {label ? <p className={styles.headerProgressLabel}>{label}</p> : null}
      <Progress
        value={value}
        max={max}
        size="sm"
        tone="neutral"
        className={styles.headerProgress}
        aria-label={ariaLabel ?? label}
      />
    </div>
  );
}

export type WizardDrawerProps = Omit<DrawerProps, 'closeGuard' | 'onCloseGuard'> & {
  /** Confirm discard when true. Default: `wizardStep > 0` when `wizardStep` set, else false. */
  dirty?: boolean;
  wizardStep?: number;
};

/** Top/side drawer shell with dirty close guard (Esc, backdrop, X, Annuler). */
export function WizardDrawer({
  opened,
  dirty: dirtyProp,
  wizardStep,
  onClose,
  children,
  ...drawerRest
}: WizardDrawerProps) {
  const dirty = dirtyProp ?? (wizardStep != null ? wizardStep > 0 : false);
  const guard = useWizardCloseGuard({ open: opened, dirty, onClose });

  return (
    <>
      <DrawerWizardRequestCloseProvider value={guard.requestClose}>
        <Drawer
          opened={opened}
          onClose={onClose}
          closeGuard={guard.closeGuard}
          onCloseGuard={guard.onCloseGuard}
          {...drawerRest}
        >
          {children}
        </Drawer>
      </DrawerWizardRequestCloseProvider>
      {guard.confirmModal}
    </>
  );
}

export type DrawerWizardNavFooterProps = {
  step: number;
  lastStep: number;
  onCancel: () => void;
  onBack: () => void;
  /**
   * Advance only — do not set form/banner errors here.
   * Field errors come from settle (blur/idle). Gate with `continueDisabled`.
   */
  onContinue: () => void;
  onSubmit?: () => void;
  submitLabel: string;
  continueLabel?: string;
  pending?: boolean;
  continuePending?: boolean;
  continueDisabled?: boolean;
  submitDisabled?: boolean;
  /** Why the primary action is disabled; shown beside it. */
  hint?: string | null;
};

export type DrawerWizardLayoutProps = {
  opened: boolean;
  /** Final close / discard (no confirm). Success paths call this directly. */
  onClose: () => void;
  title: string;
  stepIndex: number;
  stepCount: number;
  stepLabel: string;
  footer: ReactNode;
  children: ReactNode;
  /**
   * When true, Esc / backdrop / X / Annuler confirm before discard.
   * Default: `stepIndex > 0`.
   */
  dirty?: boolean;
  /** Form-level error / status — outside the traveling step pane. */
  banner?: ReactNode;
  /**
   * Extra class on the traveling step Paper (e.g. fit-content hug).
   * Prefer a stable class — swapping max-width mid StepSwitch slide can ghost.
   */
  paperClassName?: string;
  innerClassName?: string;
  bodyClassName?: string;
  /** Extra class on the Drawer panel (e.g. tour anchors). */
  className?: string;
  /** Extra class on close IconButton (e.g. hide while incomplete). */
  closeClassName?: string;
  /**
   * Header maximize/minimize (same as PO create). Default true.
   * Resets when the drawer closes (uncontrolled).
   */
  fullscreenToggle?: boolean;
  /** Controlled fullscreen sheet. When set, overrides internal toggle state. */
  fullscreen?: boolean;
  onFullscreenChange?: (next: boolean) => void;
  /** Stretch step Paper + StepSwitch to fill the drawer body (PO lines pattern). */
  fill?: boolean;
};

/** Sticky drawer footer: Annuler/Précédent + Continuer/submit. */
export function DrawerWizardNavFooter({
  step,
  lastStep,
  onCancel,
  onBack,
  onContinue,
  onSubmit,
  submitLabel,
  continueLabel = 'Continuer',
  pending = false,
  continuePending = false,
  continueDisabled = false,
  submitDisabled = false,
  hint = null,
}: DrawerWizardNavFooterProps) {
  const requestClose = useDrawerWizardRequestClose(onCancel);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      if (e.defaultPrevented || e.isComposing) return;
      if (e.shiftKey || e.metaKey || e.ctrlKey || e.altKey) return;
      if (shouldIgnoreWizardEnterTarget(e.target)) return;
      if (pending || continuePending) return;

      if (step < lastStep) {
        if (continueDisabled) return;
        e.preventDefault();
        onContinue();
        return;
      }
      if (!onSubmit || submitDisabled) return;
      e.preventDefault();
      onSubmit();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    step,
    lastStep,
    onContinue,
    onSubmit,
    pending,
    continuePending,
    continueDisabled,
    submitDisabled,
  ]);

  return (
    <Group justify="space-between" align="center" wrap="wrap" gap="sm">
      <Button
        type="button"
        variant="soft"
        onClick={step === 0 ? requestClose : onBack}
        disabled={pending || continuePending}
      >
        {step === 0 ? 'Annuler' : 'Précédent'}
      </Button>
      <Group gap="sm" align="center" wrap="nowrap" className={styles.footerEnd}>
      {hint ? (
        <Text size="sm" c="dimmed" className={styles.footerHint} role="status">
          {hint}
        </Text>
      ) : null}
      {step < lastStep ? (
        <Button
          type="button"
          variant="primary"
          onClick={onContinue}
          disabled={pending || continueDisabled}
          loading={continuePending}
        >
          {continueLabel}
        </Button>
      ) : (
        <Button
          type="button"
          variant="primary"
          onClick={onSubmit}
          disabled={pending || submitDisabled}
          loading={pending}
        >
          {submitLabel}
        </Button>
      )}
      </Group>
    </Group>
  );
}

/** Top placement drawer with title, step progress, and centered step body. */
export function DrawerWizardLayout({
  opened,
  onClose,
  title,
  stepIndex,
  stepCount,
  stepLabel,
  footer,
  children,
  dirty: dirtyProp,
  banner,
  innerClassName,
  paperClassName,
  bodyClassName,
  className,
  closeClassName,
  fullscreenToggle = true,
  fullscreen: fullscreenProp,
  onFullscreenChange,
  fill = false,
}: DrawerWizardLayoutProps) {
  const safeCount = Math.max(1, stepCount);
  const safeIndex = Math.min(Math.max(0, stepIndex), safeCount - 1);
  const dirty = dirtyProp ?? safeIndex > 0;
  const [fullscreenUncontrolled, setFullscreenUncontrolled] = useState(false);
  const controlled = fullscreenProp !== undefined;
  const fullscreen = controlled ? Boolean(fullscreenProp) : fullscreenUncontrolled;
  const setFullscreen = (next: boolean | ((prev: boolean) => boolean)) => {
    const resolved =
      typeof next === 'function' ? next(fullscreen) : next;
    if (!controlled) setFullscreenUncontrolled(resolved);
    onFullscreenChange?.(resolved);
  };
  /** Condensed chrome after sheet expand settles (spring.slow). */
  const [denseHeader, setDenseHeader] = useState(false);

  useEffect(() => {
    if (!opened) {
      if (!controlled) setFullscreenUncontrolled(false);
      setDenseHeader(false);
    }
  }, [opened, controlled]);

  useEffect(() => {
    if (!fullscreen) {
      setDenseHeader(false);
      return;
    }
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setDenseHeader(true);
      return;
    }
    const id = window.setTimeout(() => setDenseHeader(true), 200);
    return () => window.clearTimeout(id);
  }, [fullscreen]);

  const stepCaption = `Étape ${safeIndex + 1} sur ${safeCount} — ${stepLabel}`;

  return (
    <WizardDrawer
      placement="top"
      opened={opened}
      dirty={dirty}
      onClose={onClose}
      title={
        denseHeader ? (
          <span className={styles.headerTitleStack}>
            <span className={styles.headerTitleMain}>{title}</span>
            <span className={styles.headerStepHint}>{stepCaption}</span>
          </span>
        ) : (
          title
        )
      }
      headerDense={denseHeader}
      className={cx(fullscreen && styles.layoutFullscreen, className)}
      closeClassName={closeClassName}
      bodyClassName={cx(
        styles.layoutBody,
        fullscreen && styles.layoutBodyFullscreen,
        fill && styles.layoutBodyFill,
        bodyClassName,
      )}
      headerActions={
        fullscreenToggle ? (
          <IconButton
            type="button"
            variant="ghost"
            size={fullscreen ? 'sm' : 'md'}
            aria-label={fullscreen ? 'Réduire la fenêtre' : 'Agrandir en plein écran'}
            title={fullscreen ? 'Réduire' : 'Plein écran'}
            onClick={() => setFullscreen((v) => !v)}
          >
            {fullscreen ? (
              <IconArrowsMinimize size={18} stroke={1.5} aria-hidden />
            ) : (
              <IconArrowsMaximize size={20} stroke={1.5} aria-hidden />
            )}
          </IconButton>
        ) : undefined
      }
      headerBottom={
        <DrawerWizardHeaderProgress
          value={safeIndex + 1}
          max={safeCount}
          label={denseHeader ? undefined : stepCaption}
          aria-label={`Étape ${safeIndex + 1} sur ${safeCount}`}
        />
      }
      footer={footer}
    >
      <div
        className={cx(
          styles.layoutInner,
          fullscreen && styles.layoutInnerWideFullscreen,
          fill && styles.layoutInnerFill,
          innerClassName,
        )}
      >
        {banner ? <div className={styles.layoutBanner}>{banner}</div> : null}
        <DrawerWizardStepSwitch
          stepIndex={safeIndex}
          fill={fill}
          paperClassName={paperClassName}
        >
          {children}
        </DrawerWizardStepSwitch>
      </div>
    </WizardDrawer>
  );
}

export function DrawerWizardShell({
  children,
  narrow,
  className,
}: {
  children: ReactNode;
  narrow?: boolean;
  className?: string;
}) {
  return <div className={cx(styles.shell, narrow && styles.shellNarrow, className)}>{children}</div>;
}

export function DrawerWizardFooter({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <footer className={cx(styles.wizardFooter, className)}>{children}</footer>;
}

export { styles as drawerWizardStyles };
